import KeyPoints from '../../../../components/KeyPoints';
import InputField from '../../../../components/InputField';
import DateComponent from '../../../../components/DateComponent';
import FormComponent from '../../../../components/FormComponent';
import RadioComponent from '../../../../components/RadioComponent';
import CheckboxComponent from '../../../../components/CheckboxComponent';
import Request, { ApiMode } from '../../../../utils/Request';
import EventHandler from '../../../../utils/dom/event-handler';
import SelectorEngine from '../../../../utils/dom/selector-engine';

const IS_LOCAL = false;
const emailRegex =
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

const nameRegex = /^[\p{L}]+(?:[.'’-]\s?[\p{L}]+|\s[\p{L}]+)*$/u;

class HouseHoldAccountOnboarding extends FormComponent {
	constructor(element, config) {
		super(element, config);
		this.init();
	}

	init() {
		this.addBtn = SelectorEngine.findOne('.js-form-generator-next-button');
		this.isDuplicateEmailCheckInProgress = false;
		this.render();
	}

	markup(value, data, titleMarkup) {
		const { inviteBlock } = data.subComponents;
		let iconsMarkup = '';

		if (Array.isArray(inviteBlock?.socialButtons) && inviteBlock.socialButtons.length > 0) {
			iconsMarkup = inviteBlock.socialButtons
				.map(
					(btn) => `
				<a href="${btn.link}" target="_blank" rel="noopener noreferrer" class="u-inline-flex u-align-center u-justify-center">
					<lta-icon sprite-icon="${btn.icon}" width="2rem" height="2rem"></lta-icon>
				</a>
			`
				)
				.join('');
		}

		return `
			<div class="household-onboarding">
				<div class="js-radio-section u-mb-4">${titleMarkup}</div>
				<div class="js-invite-block u-bg-background-blue u-rounded u-text-center u-flex u-flex-column u-align-center u-gap-2 u-p-3 u-my-3">
					<div class="u-flex u-flex-column u-gap-1">
						<div class="u-subtitle3">${inviteBlock?.text ?? ''}</div>
						<p class="u-label1">${inviteBlock?.description ?? ''}</p>
					</div>
					<div class="u-flex u-align-center" style="gap: 10px;">
						${iconsMarkup}
					</div>
				</div>
				<hr/>
				<div class="js-email-section u-hidden u-mt-3"></div>
				<div class="js-name-section u-hidden u-mt-3"></div>
			</div>
		`;
	}

	afterRender() {
		const { subComponents } = this.data;
		const { radio, email, registrationForm } = subComponents || {};

		this.radioContainer = SelectorEngine.findOne('.js-radio-section', this.containerEl);
		this.inviteBlock = SelectorEngine.findOne('.js-invite-block', this.containerEl);
		this.emailSection = SelectorEngine.findOne('.js-email-section', this.containerEl);
		this.nameSection = SelectorEngine.findOne('.js-name-section', this.containerEl);

		this.radioComponent = new RadioComponent(this.radioContainer, {
			data: { ...radio, fieldName: radio.fieldName, answerField: 'id' }
		});

		this.radioComponent.onChange((selected) => {
			this.value = {
				fieldName: radio.fieldName,
				value: selected.id
			};
			this.clearRadioError();

			// Reset everything before showing new dependent sections
			this.resetDependentSections();
			this.isDuplicateEmailCheckInProgress = false;
			const selectedOption = radio?.options?.find((opt) => opt.id === selected.id);
			const deps = selectedOption?.dependentQuestion || [];

			this.toggleSections(deps);

			this.collectValues();

			this.toggleAddToHouseholdButton(!!selected.id);

			this.updateAddButtonState();
		});

		this.toggleSections();

		// --- Yes flow: Email field ---
		if (email) {
			this.emailInputField = new InputField(this.emailSection, {
				data: {
					...email,
					id: `${this.data.id}-email`,
					type: 'email',
					errorMessage: email.validation?.errorMessage
				}
			});

			this.findMemberBtn = document.createElement('button');
			this.findMemberBtn.type = 'button';
			this.findMemberBtn.className = 'lta-btn lta-btn--secondary lta-btn--block js-find-member-btn u-mb-3';
			this.findMemberBtn.textContent = email.buttonText;
			this.emailSection.appendChild(this.findMemberBtn);

			EventHandler.on(this.findMemberBtn, 'click', async () => {
				await this.handleFindMember(email.api);
			});
		}

		// --- No Flow: Registration Form ---
		if (registrationForm) {
			const { firstName, lastName, dateOfBirth, gender, emailInfoBlock, email } = registrationForm;

			this.firstNameField = new InputField(this.nameSection, {
				data: {
					...firstName,
					id: `${this.data.id}-fname`,
					type: 'text',
					errorMessage: firstName.validation?.errorMessage,
					hasLimit: firstName.validation?.hasLimit,
					charLimit: firstName.validation?.charLimit,
					minLengthErrorMessage: firstName.validation?.minLengthErrorMessage
				}
			});

			this.lastNameField = new InputField(this.nameSection, {
				data: {
					...lastName,
					id: `${this.data.id}-lname`,
					type: 'text',
					errorMessage: lastName.validation?.errorMessage,
					hasLimit: lastName.validation?.hasLimit,
					charLimit: lastName.validation?.charLimit,
					minLengthErrorMessage: lastName.validation?.minLengthErrorMessage
				}
			});

			this.dateOfBirthField = new DateComponent(this.nameSection, { data: dateOfBirth });

			this.dateOfBirthField.onComplete(({ valid, age }) => {
				if (!valid) return;

				if (age >= 13 && age <= 17) {
					this.childEmailInfoWrapper?.classList.remove('u-hidden');
					this.childEmailWrapper.classList.remove('u-hidden');
				} else {
					this.childEmailInfoWrapper?.classList.add('u-hidden');
					this.childEmailWrapper.classList.add('u-hidden');
					this.childEmailField.inputFieldElement.value = '';
				}

				this.collectValues();
			});

			this.genderField = new RadioComponent(this.nameSection, {
				data: {
					...gender,
					id: `${this.data.id}-gender`,
					answerField: 'id',
					errorMessage: gender.errorMessage
				}
			});

			this.genderField.onChange(() => {
				this.clearRadioError(this.genderField);
				this.collectValues();
			});

			// --- Email (for 13–17 age only) ---
			this.childEmailWrapper = document.createElement('div');
			this.childEmailWrapper.className = 'js-child-email-section u-hidden u-mt-3';

			this.childEmailField = new InputField(this.childEmailWrapper, {
				data: {
					...email,
					id: `${this.data.id}-child-email`,
					type: 'email',
					errorMessage: email.validation?.errorMessage || 'Please enter a valid email address'
				}
			});

			EventHandler.on(this.childEmailField.inputFieldElement, 'input', () => {
				this.clearInputError(this.childEmailField);
				this.collectValues();
			});

			// --- Email dupliacation check on blur ---
			EventHandler.on(this.childEmailField.inputFieldElement, 'blur', async () => {
				const emailValue = this.childEmailField.inputFieldElement.value.trim();
				if (!emailValue) return;
				this.clearInputError(this.childEmailField);
				this.duplicateEmailCheck(email, emailValue);
				this.collectValues();
			});

			this.nameSection.appendChild(this.childEmailWrapper);

			// --- Age-based visibility logic ---
			const dobInputs = SelectorEngine.find('input', this.dateOfBirthField.inputFieldContainerElement);

			if (emailInfoBlock) {
				this.childEmailInfoWrapper = document.createElement('div');
				this.childEmailInfoWrapper.className = 'js-child-email-info-section u-hidden u-mt-3';

				const keyPointsInstance = new KeyPoints(this.childEmailInfoWrapper, {
					data: emailInfoBlock
				});
				this.childEmailInfoWrapper.innerHTML = keyPointsInstance.markup(null, emailInfoBlock, '');
			}

			// Append the Info block before the email input
			if (this.childEmailInfoWrapper) {
				this.nameSection.appendChild(this.childEmailInfoWrapper);
			}
			this.nameSection.appendChild(this.childEmailWrapper);

			if (this.firstNameField) {
				this.attachNameValidation(
					this.firstNameField,
					subComponents?.registrationForm?.firstName?.validation,
					'Please enter a valid first name'
				);
			}
			if (this.lastNameField) {
				this.attachNameValidation(
					this.lastNameField,
					subComponents?.registrationForm?.lastName?.validation,
					'Please enter a valid last name'
				);
			}
		}

		if (this.firstNameField) {
			EventHandler.on(this.firstNameField.inputFieldElement, 'input', () => {
				this.clearInputError(this.firstNameField);
				this.collectValues();
			});
		}

		if (this.lastNameField) {
			EventHandler.on(this.lastNameField.inputFieldElement, 'input', () => {
				this.clearInputError(this.lastNameField);
				this.collectValues();
			});
		}

		if (this.dateOfBirthField?.inputFieldContainerElement) {
			const dobInputs = SelectorEngine.find('input', this.dateOfBirthField.inputFieldContainerElement);
			dobInputs.forEach((input) => {
				EventHandler.on(input, 'input', () => {
					setTimeout(() => this.collectValues(), 100);
				});
			});
		}

		const infoBlockData = this.data.subComponents?.infoBlock;
		if (infoBlockData) {
			this.infoBlockWrapper = document.createElement('div');
			this.infoBlockWrapper.className = 'js-info-block-section u-mt-3 u-hidden';

			const infoKeyPoints = new KeyPoints(this.infoBlockWrapper, {
				data: infoBlockData
			});
			this.infoBlockWrapper.innerHTML = infoKeyPoints.markup(null, infoBlockData, '');

			const parentContainer = SelectorEngine.findOne('.household-onboarding', this.containerEl);
			if (parentContainer) {
				parentContainer.appendChild(this.infoBlockWrapper);
			}
		}

		this.toggleAddToHouseholdButton(false);
	}

	toggleAddToHouseholdButton(show) {
		if (!this.addBtn) return;
		this.addBtn.classList.toggle('u-hidden', !show);

		// Show or hide info block along with button
		if (this.infoBlockWrapper) {
			if (show) this.infoBlockWrapper.classList.remove('u-hidden');
			else this.infoBlockWrapper.classList.add('u-hidden');
		}
	}


	setAddButtonDisabled(disabled) {
		if (!this.addBtn) return;
		this.addBtn.disabled = disabled;
	}

	updateAddButtonState() {
		if (!this.addBtn) return;

		// If async email check is running, NEVER change button state
		if (this.isDuplicateEmailCheckInProgress) {
			this.addBtn.disabled = true;
			return;
		}

		const selected = this.radioComponent?.value?.id;

		// --- YES flow (existing account) ---
		if (selected === 'yes') {
			const hasSelectableMembers =
				this.checkboxComponent &&
				Array.isArray(this.checkboxComponent.value?.value) &&
				this.checkboxComponent.value.value.length > 0;

			this.addBtn.disabled = !hasSelectableMembers;
			return;
		}

		// --- NO flow (new account / registration) ---
		if (selected === 'no') {
			// Button enablement is driven by form validation,
			// so never keep it force-disabled here
			this.addBtn.disabled = false;
		}
	}

	toggleSections(dependentSections = []) {
		const sectionMap = {};

		Object.keys(this.data.subComponents || {}).forEach((key) => {
			const sectionElement = this.getSectionElementByKey(key);
			if (sectionElement) sectionMap[key] = sectionElement;
		});

		Object.values(sectionMap).forEach((section) => {
			if (section) {
				section.classList.add('u-hidden');
				section.classList.remove('u-flex');
			}
		});

		if (Array.isArray(dependentSections) && dependentSections.length > 0) {
			dependentSections.forEach((sectionKey) => {
				const section = sectionMap[sectionKey];
				if (section) {
					section.classList.remove('u-hidden');
					if (sectionKey === 'inviteBlock') section.classList.add('u-flex');
				}
			});
		} else if (this.inviteBlock) {
			this.inviteBlock.classList.remove('u-hidden');
			this.inviteBlock.classList.add('u-flex');
		}
	}

	getSectionElementByKey(key) {
		switch (key) {
			case 'inviteBlock':
				return this.inviteBlock;
			case 'email':
				return this.emailSection;
			case 'registrationForm':
				return this.nameSection;
			default:
				return null;
		}
	}

	resetDependentSections() {
		[this.emailInputField, this.firstNameField, this.lastNameField, this.childEmailField, this.genderField].forEach(
			(field) => {
				if (!field) return;

				if (field.inputFieldElement) {
					field.inputFieldElement.value = '';
					this.clearInputError(field);
				} else if (field.value) {
					field.value = { id: '', label: '' };
					const selectedInputs = field.containerEl?.querySelectorAll('input[type="radio"]:checked');
					selectedInputs?.forEach((input) => (input.checked = false));
					const errNode = field.containerEl?.querySelector('.lta-text-input--error');
					if (errNode) errNode.textContent = '';
				}
			}
		);

		this.clearRadioError(this.genderField);

		if (this.dateOfBirthField?.inputFieldElements) {
			this.dateOfBirthField.inputFieldElements.forEach((input) => (input.value = ''));
			this.dateOfBirthField.removeInvalidClass?.();
			this.dateOfBirthField.dateObject = { day: '', month: '', year: '' };
		}

		this.childEmailWrapper?.classList.add('u-hidden');
		this.childEmailInfoWrapper?.classList.add('u-hidden');

		const existingWrapper = this.emailSection?.querySelector('.js-member-results-wrapper');
		if (existingWrapper) existingWrapper.remove();
		this.checkboxComponent = null;

		this.emailSection?.classList.add('u-hidden');
		this.nameSection?.classList.add('u-hidden');

		this.value = {
			fieldName: 'householdAccountSetup',
			value: {
				hasExistingAccount: '',
				emailId: '',
				firstName: '',
				lastName: '',
				dateOfBirth: '',
				selectedMembers: []
			}
		};
	}

	async handleFindMember(api) {
		const existingWrapper = this.emailSection?.querySelector('.js-member-results-wrapper');
		if (existingWrapper) {
			const cards = existingWrapper.querySelectorAll('.lta-card');
			cards.forEach((card, index) => {
				card.style.transitionDelay = `${index * 100}ms`;
			});
			existingWrapper.classList.add('fade-out');
			await new Promise((resolve) => setTimeout(resolve, 300 + cards.length * 100));
			existingWrapper.remove();
		}
		this.checkboxComponent = null;

		let shimmerWrapper = document.createElement('div');
		shimmerWrapper.className = 'js-member-shimmer';
		for (let i = 0; i < 3; i++) {
			const line = document.createElement('div');
			line.className = 'shimmer-line';
			shimmerWrapper.appendChild(line);
		}
		this.emailSection.appendChild(shimmerWrapper);

		this.email = this.emailInputField?.inputFieldElement?.value?.trim();

		// Create or show email loader spinner
		const emailInput = SelectorEngine.findOne('.lta-text-input', this.emailSection);

		const wrapper = document.createElement('div');
		wrapper.classList.add('input-with-loader');
		emailInput.parentNode.insertBefore(wrapper, emailInput);
		wrapper.appendChild(emailInput);

		const loader = document.createElement('span');
		loader.className = 'lta-field-loader';
		wrapper.appendChild(loader);

		if (!this.email) {
			this.showInputError(
				this.emailInputField,
				this.data.subComponents?.email?.validation?.errorMessage || 'Please enter a email address'
			);
			return;
		}

		if (!emailRegex.test(this.email)) {
			this.showInputError(
				this.emailInputField,
				this.data.subComponents?.email?.validation?.invalidErrorMessage || 'Please enter a valid email address'
			);
			return;
		}

		loader.style.display = 'inline-block';
		emailInput.style.pointerEvents = 'none';

		this.findMemberBtn.disabled = true; // Prevent double clicks
		this.addBtn.disabled = true;
		try {
			const response = await Request.post(
				api.url,
				{ email: this.email },
				{ local: IS_LOCAL, req: { apiMode: ApiMode.LtaError } }
			);

			loader.style.display = 'none';
			emailInput.style.pointerEvents = '';
			this.findMemberBtn.disabled = false;

			const shimmer = this.emailSection.querySelector('.js-member-shimmer');
			if (shimmer) shimmer.remove();

			if (response.success) {
				const { members, alreadyLinked, checkboxConfig, translation } = response.data;

				this.renderMemberResults(members, alreadyLinked, checkboxConfig, translation);

				const checkboxes = this.emailSection.querySelectorAll('.js-member-checkbox');
				checkboxes.forEach((cb) =>
					EventHandler.on(cb, 'change', () => {
						this.collectValues();
					})
				);
				this.updateAddButtonState();
			} else {
				this.showInputError(
					this.emailInputField,
					response.errorMessage || "We couldn't find an account with that email. Please try again"
				);
			}
		} catch (error) {
			this.addBtn.disabled = false;
			loader.style.display = 'none';
			emailInput.style.pointerEvents = '';
			this.findMemberBtn.disabled = false;
			console.error('handleFindMember error:', error);
			this.showInputError(this.emailInputField, 'Something went wrong');
		}
	}

	renderMemberResults(members = [], alreadyLinked = [], checkboxConfig, translation) {
		const existingWrapper = this.emailSection.querySelector('.js-member-results-wrapper');
		if (existingWrapper) existingWrapper.remove();

		const wrapper = document.createElement('div');
		wrapper.className = 'js-member-results-wrapper u-mt-3';

		if (members.length === 0 && alreadyLinked.length === 0) {
			wrapper.innerHTML = `<p class="u-body2 u-text-status-red">No member found for this email.</p>`;
			this.emailSection.insertBefore(wrapper, this.emailAcceptanceEl || null);
			return;
		}

		if (members.length > 0) {
			// --- Checkbox config for selectable members ---
			const configData = {
				id: checkboxConfig.id,
				fieldName: checkboxConfig.fieldName,
				questionType: checkboxConfig.questionType,
				instructionText: `${checkboxConfig.instructionText}`,
				class: 'u-mb-2',
				errorMessage: checkboxConfig.errorMessage,
				validation: {
					errorMessage: checkboxConfig.errorMessage
				},
				minSelectable: checkboxConfig.minSelectable,
				minSelectableErrorMessage: checkboxConfig.minSelectableErrorMessage,
				value: members.map((member) => member.id).join(','),
				options: members.map((member) => ({
					id: member.id,
					label: `
                    <div class="lta-card lta-card--light u-flex u-flex-column u-bg-background-blue u-border-0 u-rounded u-px-3 u-py-2 u-w-100">
                        <span class="u-caption2 u-fw-bold">${member.firstName} ${member.lastName}</span>
                        <span class="u-body2">${translation.age}: ${member.age}</span>
                        ${member.age < 13 ? `<span class="u-body2">${translation.gender}: ${member.gender}</span>` : ''}
                        <span class="u-body2">${translation.ltaNumber}: ${member.ltaNumber}</span>
                    </div>
                `,
					value: member
				}))
			};

			// --- Render selectable members ---
			this.checkboxComponent = new CheckboxComponent(wrapper, { data: configData });
			wrapper.innerHTML = this.checkboxComponent.markup(null, this.checkboxComponent.data, '');
			this.checkboxComponent.containerEl = wrapper;

			this.checkboxComponent.initEventListeners();
			this.checkboxComponent.afterRender?.();

			this.checkboxComponent.handleCheckboxClick(false);
			this.collectValues();
		}
		// --- Appending 'already linked' members if available ---
		if (alreadyLinked.length > 0) {
			const infoText = document.createElement('p');
			infoText.className = 'u-body1 u-text-grey-dark u-mb-2';
			infoText.textContent = translation.alreadyLinkedMembersInfoText;
			wrapper.appendChild(infoText);

			alreadyLinked.forEach((member) => {
				const card = document.createElement('div');
				card.className =
					'lta-card lta-card--disabled u-flex u-flex-column u-gap-1 u-px-3 u-py-2 u-border-0 u-rounded u-bg-background-blue u-mb-2';
				card.innerHTML = `
				<div>
					<span class="u-caption2 u-fw-bold">${member.firstName} ${member.lastName}</span><br/>
					<span class="u-body2">${translation.age}: ${member.age}</span><br/>
					${member.age < 13 ? `<span class="u-body2">${translation.gender}: ${member.gender}</span><br/>` : ''}
					<span class="u-body2">${translation.ltaNumber}: ${member.ltaNumber}</span>
				</div>
			`;
				wrapper.appendChild(card);
			});
		}

		if (this.emailAcceptanceEl) {
			this.emailSection.insertBefore(wrapper, this.emailAcceptanceEl);
		} else {
			this.emailSection.appendChild(wrapper);
		}

		EventHandler.on(wrapper, 'change', () => {
			this.checkboxComponent.validate();
			this.collectValues();
			this.updateAddButtonState();
		});

		requestAnimationFrame(() => {
			const cards = wrapper.querySelectorAll('.lta-card');
			cards.forEach((card, index) => {
				card.style.transitionDelay = `${index * 100}ms`;
			});
			wrapper.classList.add('fade-in');
		});
	}

	showRadioError(message, targetField = this.radioComponent) {
		if (!targetField) return;

		const wrapper = targetField.containerEl?.querySelector('.lta-form-check-group');

		if (!wrapper) return;

		const errNode = wrapper?.querySelector('.lta-text-input--error');

		if (errNode) {
			errNode.textContent = message;
			errNode.style.display = 'block';
		}

		wrapper.classList.add('is-invalid', 'is-focused');
	}

	clearRadioError(targetField = this.radioComponent) {
		const wrapper = targetField?.containerEl?.querySelector('.lta-form-check-group');
		if (!wrapper) return;

		const errNode = wrapper?.querySelector('.lta-text-input--error');
		if (errNode) {
			errNode.textContent = '';
			errNode.style.display = '';
		}

		wrapper.classList.remove('is-invalid', 'is-focused');
	}

	showInputError(inputField, message) {
		if (!inputField?._element) return;

		const wrapper = inputField.inputFieldElement?.closest(
			'.lta-text-input__wrapper, .lta-input-field, .lta-form-check-group'
		);

		const errorEl = wrapper?.querySelector('.lta-text-input--error');

		if (errorEl) {
			errorEl.textContent = message;
			errorEl.style.display = 'block';
		}

		if (wrapper) {
			wrapper.classList.add('is-invalid', 'is-focused');
		}
	}

	clearInputError(inputField) {
		if (!inputField?._element) return;

		const wrapper = inputField.inputFieldElement?.closest(
			'.lta-text-input__wrapper, .lta-input-field, .lta-form-check-group'
		);
		const errorEl = wrapper?.querySelector('.lta-text-input--error');

		if (errorEl) {
			errorEl.textContent = '';
			errorEl.style.display = '';
		}

		if (wrapper) {
			wrapper.classList.remove('is-invalid', 'is-focused');
		}
	}

	attachNameValidation(field, validation, defaultMessage) {
		if (!field?.inputFieldElement) return;

		const validateName = () => {
			const value = field.inputFieldElement.value.trim();
			const disallowSpecials = validation?.allowNumbersOrSpecialCharacters === false;
			let invalid = disallowSpecials && !nameRegex.test(value);

			if (invalid) {
				this.showInputError(field, validation?.invalidErrorMessage || defaultMessage);
			} else {
				const charLimit = validation?.charLimit ?? validation?.minlength ?? validation?.minLength ?? null;

				if (charLimit && value.length > 0 && value.length < charLimit) {
					this.showInputError(
						field,
						validation?.minLengthErrorMessage ||
							validation?.minlengthErrorMessage ||
							`Must be at least ${charLimit} characters`
					);
					invalid = true;
				}
			}

			if (!invalid) {
				this.clearInputError(field);
			}
			this.collectValues();
		};

		EventHandler.on(field.inputFieldElement, 'input', validateName);
		EventHandler.on(field.inputFieldElement, 'blur', validateName);
	}

	async duplicateEmailCheck(emailConfig, emailId) {
		const emailInput = this.childEmailField.inputFieldElement;

		this.isDuplicateEmailCheckInProgress = true;
		this.setAddButtonDisabled(true); // block submission while API runs

		const wrapper = document.createElement('div');
		wrapper.classList.add('input-with-loader');
		emailInput.parentNode.insertBefore(wrapper, emailInput);
		wrapper.appendChild(emailInput);

		const loader = document.createElement('span');
		loader.className = 'lta-field-loader';
		wrapper.appendChild(loader);

		if (!emailRegex.test(emailId)) {
			this.showInputError(
				this.childEmailField,
				this.data.subComponents?.registrationForm?.email.validation?.invalidErrorMessage ||
					'Please enter a valid email address'
			);
			return;
		}

		loader.style.display = 'inline-block';
		emailInput.style.pointerEvents = 'none';

		try {
			const response = await Request.post(
				emailConfig.api.url,
				{ emailId },
				{ local: IS_LOCAL, req: { apiMode: ApiMode.LtaError } }
			);

			loader.style.display = 'none';
			emailInput.style.pointerEvents = '';

			const { accountExists, statusCode } = response;

			// Expected structure: { accountExists: true/false, statusCode: '00' | '01' | '99' }
			if (accountExists) {
				this.showInputError(
					this.childEmailField,
					emailConfig.api.duplicateEmailError || 'This email is already registered. Try another email address.'
				);
				this.childEmailValid = false;
			} else if (!accountExists && statusCode === '99') {
				this.showInputError(
					this.childEmailField,
					emailConfig.api.apiFailError || 'Something went wrong please try again'
				);
				this.childEmailValid = false;
			} else if (accountExists === false && statusCode === '00') {
				this.clearInputError(this.childEmailField);
				this.childEmailValid = true;
			} else {
				this.showInputError(this.childEmailField, 'Unexpected response from server');
				this.childEmailValid = false;
			}
		} catch (error) {
			console.error('Email check API failed:', error);
			this.showInputError(
				this.childEmailField,
				emailConfig.api.apiFailError || 'Something went wrong please try again'
			);
			this.childEmailValid = false;
		} finally {
			loader.style.display = 'none';
			emailInput.style.pointerEvents = '';
			this.isDuplicateEmailCheckInProgress = false;
			this.setAddButtonDisabled(false);
		}
	}

	collectValues() {
		const selectedMembers = Array.isArray(this.checkboxComponent?.value?.value)
			? this.checkboxComponent.value.value.map((item) => item.value)
			: [];
		this.value = {
			fieldName: 'householdAccountSetup',
			value: {
				hasExistingAccount: this.radioComponent?.value?.id || '',
				emailId:
					this.childEmailField?.inputFieldElement?.value?.trim() ||
					this.emailInputField?.inputFieldElement?.value?.trim() ||
					'',
				firstName: this.firstNameField?.inputFieldElement?.value?.trim() || '',
				lastName: this.lastNameField?.inputFieldElement?.value?.trim() || '',
				dateOfBirth: this.dateOfBirthField?.dateObject
					? `${this.dateOfBirthField.dateObject.day}/${this.dateOfBirthField.dateObject.month}/${this.dateOfBirthField.dateObject.year}`
					: '',
				gender: this.genderField?.value?.id || '',
				selectedMembers
			}
		};

		this.updateAddButtonState();
	}

	async validate() {
		const selected = this.radioComponent?.value?.id;
		const { subComponents } = this.data || {};

		this.clearRadioError();

		// --- Validate radio first ---
		if (!selected) {
			this.showRadioError(subComponents?.radio?.errorMessage || 'Please select an option');
			return false;
		}

		const selectedOption = subComponents?.radio?.options?.find((opt) => opt.id === selected);
		const deps = selectedOption?.dependentQuestion || [];
		if (!Array.isArray(deps) || deps.length === 0) return true;

		let isValid = true;

		for (const key of deps) {
			switch (key) {
				case 'email': {
					const emailValue = this.emailInputField?.inputFieldElement?.value?.trim();
					if (!emailValue) {
						this.showInputError(
							this.emailInputField,
							subComponents?.email?.validation?.errorMessage || 'Please enter a valid email'
						);
						isValid = false;
						break;
					}

					if (!this.checkboxComponent) {
						await this.handleFindMember(subComponents.email.api);
						return false; // stopping form submission until members are displayed
					}

					const selectedMembers =
						Array.isArray(this.checkboxComponent?.value?.value) && this.checkboxComponent.value.value.length > 0
							? this.checkboxComponent.value.value
							: [];

					if (selectedMembers.length === 0) {
						this.checkboxComponent.validate();
						isValid = false;
					}

					const valid = this.checkboxComponent.validate();
					if (!valid) isValid = false;
					break;
				}
				case 'registrationForm': {
					const firstName = this.firstNameField?.inputFieldElement?.value?.trim();
					const lastName = this.lastNameField?.inputFieldElement?.value?.trim();

					const firstVal = subComponents?.registrationForm?.firstName?.validation || {};
					const lastVal = subComponents?.registrationForm?.lastName?.validation || {};

					const firstMin =
						firstVal?.charLimit ?? firstVal?.minlength ?? firstVal?.minLength ?? (firstVal?.hasLimit ? 0 : undefined);

					const lastMin =
						lastVal?.charLimit ?? lastVal?.minlength ?? lastVal?.minLength ?? (lastVal?.hasLimit ? 0 : undefined);

					if (!firstName) {
						this.showInputError(
							this.firstNameField,
							subComponents?.registrationForm?.firstName?.validation?.errorMessage || 'Please enter a first name'
						);
						isValid = false;
					}

					if (!lastName) {
						this.showInputError(
							this.lastNameField,
							subComponents?.registrationForm?.lastName?.validation?.errorMessage || 'Please enter a last name'
						);
						isValid = false;
					}

					if (firstName && typeof firstMin === 'number' && firstMin > 0 && firstName.length < firstMin) {
						const msg =
							firstVal?.minLengthErrorMessage ||
							firstVal?.minlengthErrorMessage ||
							`First name must be ${firstMin} or more letters`;
						this.showInputError(this.firstNameField, msg);
						isValid = false;
					}

					if (lastName && typeof lastMin === 'number' && lastMin > 0 && lastName.length < lastMin) {
						const msg =
							lastVal?.minLengthErrorMessage ||
							lastVal?.minlengthErrorMessage ||
							`Last name must be ${lastMin} or more letters`;
						this.showInputError(this.lastNameField, msg);
						isValid = false;
					}

					if (
						subComponents?.registrationForm?.firstName?.validation?.allowNumbersOrSpecialCharacters == false &&
						!nameRegex.test(firstName)
					) {
						this.showInputError(
							this.firstNameField,
							subComponents?.registrationForm?.firstName?.validation?.invalidErrorMessage ||
								'Please enter a valid first name'
						);
						isValid = false;
					}

					if (
						subComponents?.registrationForm?.lastName?.validation?.allowNumbersOrSpecialCharacters == false &&
						!nameRegex.test(lastName)
					) {
						this.showInputError(
							this.lastNameField,
							subComponents?.registrationForm?.lastName?.validation?.invalidErrorMessage ||
								'Please enter a valid last name'
						);
						isValid = false;
					}

					const dobValid = this.dateOfBirthField.validate();

					const isChildEmailVisible =
						this.childEmailWrapper &&
						!this.childEmailWrapper.classList.contains('u-hidden');

					if (!dobValid) {
						isValid = false;
					} else {
						// email logic based on age
						const age = this.dateOfBirthField.getAge();

						if (age >= 13 && age <= 17 && isChildEmailVisible) {
							const emailValue =
								this.childEmailField?.inputFieldElement?.value?.trim();
							if (!emailValue) {
								this.showInputError(this.childEmailField,'Please enter a valid email address');
								isValid = false;
							}
						}
					}
					const genderValue = this.genderField?.value?.id;
					if (!genderValue) {
						this.showRadioError(
							subComponents?.registrationForm?.gender?.errorMessage || 'Please select a gender',
							this.genderField
						);
						isValid = false;
					}

					// --- If duplicate email error is visible preventing submitting the form ---
					if (isChildEmailVisible) {
						if (!this.childEmailValid) isValid = false;
					}
					break;
				}
				default:
					break;
			}
		}
		return isValid;
	}

	shouldShowNext() {
		return !!this.radioComponent?.value?.id;
	}
}

export default HouseHoldAccountOnboarding;
/* eslint-enable unicorn/no-null */
